import React, { useEffect, useRef, useState } from 'react';
import Select, { components, type DropdownIndicatorProps, type MenuListProps, type StylesConfig } from 'react-select';
import clsx from 'clsx';

export interface IRingSizeOption {
  value: string;
  label: string;
}

interface IProps {
  inputId?: string;
  label: string;
  options: IRingSizeOption[];
  value: string;
  onChange: (value: string) => void;
  loading?: boolean;
}

// react-select sets its own cosmetic styles via emotion, so anything that has to
// beat a default lives here rather than in the stylesheet.
const selectStyles: StylesConfig<IRingSizeOption, false> = {
  control: (base) => ({
    ...base,
    minHeight: 0,
    border: 'none',
    borderRadius: 0,
    backgroundColor: 'transparent',
    boxShadow: 'none',
    cursor: 'pointer'
  }),
  valueContainer: (base) => ({ ...base, padding: '25px 0 7px 14px' }),
  singleValue: (base) => ({
    ...base,
    margin: 0,
    color: 'var(--primary-color)',
    fontFamily: 'inherit',
    fontSize: '1.125rem',
    lineHeight: 1.45
  }),
  input: (base) => ({ ...base, margin: 0, padding: 0, color: 'var(--primary-color)', fontSize: '1.125rem' }),
  placeholder: (base) => ({ ...base, margin: 0, fontSize: '1.125rem' }),
  indicatorsContainer: (base) => ({ ...base, paddingRight: 14 }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: 0,
    color: 'var(--primary-color)',
    ':hover': { color: 'var(--primary-color)' }
  }),
  menu: (base) => ({ ...base, marginTop: 4, borderRadius: 8, overflow: 'hidden' }),
  // Portalled out of the builder's scrolling panel, so it needs to clear the app chrome.
  menuPortal: (base) => ({ ...base, zIndex: 1100 }),
  option: (base, { isSelected, isFocused }) => ({
    ...base,
    backgroundColor: isSelected ? 'var(--brand-color)' : isFocused ? '#e6eef4' : 'transparent',
    color: isSelected ? '#fff' : 'var(--primary-color)',
    cursor: 'pointer'
  })
};

const Chevron = (props: DropdownIndicatorProps<IRingSizeOption, false>) => (
  <components.DropdownIndicator {...props}>
    <svg width="12" height="8" viewBox="0 0 12 8" aria-hidden="true" focusable="false">
      <path d="M1 1.5 6 6.5l5-5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </components.DropdownIndicator>
);

// react-select only scrolls the selected option just far enough to be visible, which
// on a long size list leaves it pinned to an edge. Centre it instead.
const MenuList = (props: MenuListProps<IRingSizeOption, false>) => {
  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Deferred a frame so it lands after react-select's own scroll-into-view pass.
    const frame = requestAnimationFrame(() => {
      const list = listRef.current;
      const selected = list?.querySelector<HTMLElement>('.ring-size-select__option--is-selected');
      if (list && selected) {
        list.scrollTop = selected.offsetTop - (list.clientHeight - selected.offsetHeight) / 2;
      }
    });
    return () => { cancelAnimationFrame(frame); };
  }, []);

  return (
    <components.MenuList
      {...props}
      innerRef={(el: HTMLDivElement | null) => {
        listRef.current = el;
        if (typeof props.innerRef === 'function') props.innerRef(el);
      }}
    />
  );
};

const RingSizeSelect = ({ inputId = 'ring-size-select', label, options, value, onChange, loading = false }: IProps) => {
  const [isClickFocus, setIsClickFocus] = useState(false);
  const selected = options.find((option) => option.value === value) ?? null;

  return (
    <div
      className={clsx('ring-size__field', { loading, 'is-click-focus': isClickFocus })}
      onPointerDown={() => { setIsClickFocus(true); }}
      onBlur={() => { setIsClickFocus(false); }}
    >
      <label htmlFor={inputId} className="ring-size__label">{label}</label>
      <Select
        inputId={inputId}
        classNamePrefix="ring-size-select"
        options={options}
        value={selected}
        onChange={(option) => { if (option) onChange(option.value); }}
        isDisabled={!options.length}
        isClearable={false}
        // Sizes are short numbers you scroll to; searching isn't worth the
        // keyboard covering the list on mobile.
        isSearchable={false}
        placeholder={options.length ? '' : 'Loading sizes…'}
        components={{ DropdownIndicator: Chevron, IndicatorSeparator: null, MenuList }}
        styles={selectStyles}
        menuPlacement="auto"
        menuPortalTarget={document.body}
      />
    </div>
  );
};

export default RingSizeSelect;
